import { useApi } from "@/hooks/useApi";
import { IntegrationCrmSeliarisStatusDTO } from "@shaarp/common";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type SeliarisIntegrationContextType = {
  seliarisIntegration: IntegrationCrmSeliarisStatusDTO | null;
  isLoading: boolean;
  /**
   * True when the last read of the status failed. Distinct from
   * `seliarisIntegration === null`, which means "this team has no Seliaris
   * connection" — a fact, where this one means "we do not know".
   */
  hasLoadFailed: boolean;
  refetch: () => Promise<void>;
};

const SeliarisIntegrationContext =
  createContext<SeliarisIntegrationContextType>({
    seliarisIntegration: null,
    isLoading: true,
    hasLoadFailed: false,
    refetch: async () => {},
  });

export const useSeliarisIntegration = (): SeliarisIntegrationContextType => {
  const context = useContext(SeliarisIntegrationContext);
  if (!context) {
    throw new Error(
      "useSeliarisIntegration must be used within a SeliarisIntegrationProvider",
    );
  }
  return context;
};

/**
 * Seliaris connection status, mirroring `HubSpotIntegrationProvider`.
 *
 * A context rather than a React Query hook because the integration cards read
 * three of these synchronously side by side; adding a fourth shape there would
 * make the cards the only place in the app mixing both styles.
 *
 * The status never carries the API key — only its last four characters — so
 * holding it in a context is not holding a credential.
 */
export const SeliarisIntegrationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const api = useApi();
  const [seliarisIntegration, setSeliarisIntegration] =
    useState<IntegrationCrmSeliarisStatusDTO | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasLoadFailed, setHasLoadFailed] = useState<boolean>(false);

  const fetchSeliarisIntegration = useCallback(async (): Promise<void> => {
    try {
      const { data } = await api.get<IntegrationCrmSeliarisStatusDTO>(
        "/integration/crm/seliaris",
      );
      setSeliarisIntegration(data);
      setHasLoadFailed(false);
    } catch (error) {
      // A failed read is not an answer. Turning it into "not connected" used to
      // show a connected team the API-key form again — and made every "send to
      // the CRM" button vanish without a word. The last known status is kept,
      // and the failure is said out loud instead.
      console.error("Failed to read the Seliaris integration status", error);
      setHasLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  const refetch = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    await fetchSeliarisIntegration();
  }, [fetchSeliarisIntegration]);

  useEffect(() => {
    fetchSeliarisIntegration();
  }, [fetchSeliarisIntegration]);

  const value = useMemo(
    () => ({ seliarisIntegration, isLoading, hasLoadFailed, refetch }),
    [seliarisIntegration, isLoading, hasLoadFailed, refetch],
  );

  return (
    <SeliarisIntegrationContext.Provider value={value}>
      {children}
    </SeliarisIntegrationContext.Provider>
  );
};
